import { getAbilityData } from '../content/AbilityDataAdapter';

/**
 * Healing auras (lane AM follow-up, 2026-10-09) — abilities that pulse
 * healing to allies in a radius for a while: a planted totem, or an aura
 * that follows its caster. The server runs them (ServerHealAuras) from the
 * accepted cast, so no client message can ask for a heal; the client reads
 * the same table for the aura's look and its offline-only local heal.
 */
export interface HealAuraCaster {
  intelligence?: number;
  magicAttack?: number;
  stats?: { intelligence?: number; magicAttack?: number };
}

export interface HealAuraSpec {
  radius: number;
  tickRateMs: number;
  durationMs: number;
  /** 'world' stays at the cast point; 'caster' follows the caster. */
  anchor: 'world' | 'caster';
  /** Healing per tick to each ally in the radius. */
  amount: (caster: HealAuraCaster) => number;
}

const stat = (caster: HealAuraCaster, key: 'intelligence' | 'magicAttack'): number =>
  caster.stats?.[key] ?? caster[key] ?? 0;

const totem = getAbilityData('healing_stream_totem');

export const HEAL_AURAS: Readonly<Record<string, HealAuraSpec>> = Object.freeze({
  healing_stream_totem: {
    radius: totem?.radius ?? 120,
    tickRateMs: 1000,
    durationMs: totem?.durationMs ?? 8000,
    anchor: 'world',
    amount: (caster: HealAuraCaster) => Math.floor(
      (totem?.baseDamage ?? 8) + stat(caster, 'magicAttack') * 0.2 + stat(caster, 'intelligence') * 0.4,
    ),
  },
  aura_of_devotion: {
    radius: 120,
    tickRateMs: 2000,
    durationMs: 10000,
    anchor: 'caster',
    amount: () => 50,
  },
});

export function healAuraSpec(abilityId: string): HealAuraSpec | undefined {
  return Object.prototype.hasOwnProperty.call(HEAL_AURAS, abilityId) ? HEAL_AURAS[abilityId] : undefined;
}
